import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { createChart, CrosshairMode } from 'lightweight-charts';
import ApiClient from '../api/ApiClient';

const ACTION_LABELS = {
  Buy: 'Buy',
  Sell: 'Sell',
  BuyMoneyMarket: 'Buy Money Market',
  SellMoneyMarket: 'Sell Money Market',
};

const ACTION_BADGE = {
  Buy: 'success',
  Sell: 'danger',
  BuyMoneyMarket: 'info',
  SellMoneyMarket: 'warning',
  Dividend: 'primary',
};

const SIGNAL_BADGE = {
  Buy: 'success',
  Sell: 'danger',
  Money: 'info',
};

export const PerformanceCalculation = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // ── Form state ─────────────────────────────────────────────────────────────
  const [allModels, setAllModels] = useState([]);
  // Each row is { model: { value, label, signalTags, activeSignalTag },
  //                 tag: { value, label } | null }.
  // The user picks one model per row AND one tag from that model's
  // SignalTags — the system no longer auto-picks model.ActiveSignalTag.
  const [selectedModelRows, setSelectedModelRows] = useState([]);

  const [stocks, setStocks] = useState([{ tickerId: null, percentage: 100 }]);
  const [equalShare, setEqualShare] = useState(true);
  const [moneyMarketTicker, setMoneyMarketTicker] = useState(null);
  const [initialAmount, setInitialAmount] = useState(10000);

  // ── Date range ─────────────────────────────────────────────────────────────
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // ── FastTrack options ──────────────────────────────────────────────────────
  const [useFastTrack, setUseFastTrack] = useState(false);
  const [useDividendAdjusted, setUseDividendAdjusted] = useState(false);

  // ── Commission ────────────────────────────────────────────────────────────
  const [commissionPercent, setCommissionPercent] = useState(0.2);

  // ── Signal combination mode ────────────────────────────────────────────────
  const COMBINE_MODE_OPTIONS = [
    { value: 'CbSoM', label: 'cBSoM — unanimous Buy / unanimous Sell / Mixed → Money (default)' },
    { value: 'CBoS',  label: 'cBoS — Buy only when all agree Buy, otherwise Sell' },
    { value: 'CSoB',  label: 'cSoB — Sell only when all agree Sell, otherwise Buy' },
  ];
  const [combineMode, setCombineMode] = useState(COMBINE_MODE_OPTIONS[0]);

  // ── Save settings state ───────────────────────────────────────────────────
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveSettingsName, setSaveSettingsName] = useState('');
  const [saveSettingsId, setSaveSettingsId] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [saveSettingsError, setSaveSettingsError] = useState('');
  const [loadedSettingsName, setLoadedSettingsName] = useState(null);

  // ── Results state ──────────────────────────────────────────────────────────
  const [result, setResult] = useState(null);
  const [buyAndHoldResult, setBuyAndHoldResult] = useState(null);
  const [buyAndHoldIndexResult, setBuyAndHoldIndexResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [buyAndHoldIndexTicker, setBuyAndHoldIndexTicker] = useState(null);
  const [error, setError] = useState(null);
  const [portfolioView, setPortfolioView] = useState('chart'); // 'chart' | 'table'
  const [chartScale, setChartScale] = useState('lin'); // 'lin' | 'log'
  const [chartType] = useState('linear'); // 'linear' | 'exponential'

  // ── UI state ──────────────────────────────────────────────────────────────
  const [requestBuyAndHold, setRequestBuyAndHold] = useState(false);
  const [requestBuyAndHoldIndex, setRequestBuyAndHoldIndex] = useState(false);

  // Collapsible sections
  const [showModelSignals, setShowModelSignals] = useState(true);
  const [showCombinedSignals, setShowCombinedSignals] = useState(true);
  const [showTrades, setShowTrades] = useState(true);
  const [showPortfolio, setShowPortfolio] = useState(true);

  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const annualReturnChartContainerRef = useRef(null);
  const annualReturnChartRef = useRef(null);

  // ── Load models and tickers on mount ──────────────────────────────────────
  useEffect(() => {
    async function loadData() {
      try {
        const modelsData = await ApiClient.searchModels({ Offset: 0, Limit: 1000 });
        setAllModels((modelsData.Items || []).map(m => ({
          value: m.Id,
          label: `${m.Name || m.FileName} [#${m.Id}]`,
          signalTags: m.SignalTags || [],
          activeSignalTag: m.ActiveSignalTag || null,
        })));
      } catch (exc) {
        console.error('Failed to load models/tickers', exc);
      }
    }
    loadData();
  }, []);

  // ── Load settings from navigation state (from settings page) ──────────────
  useEffect(() => {
    const ls = location.state?.loadSettings;
    if (!ls) return;
    const s = ls.Settings;
    if (!s) return;

    // Rebuild the (model, tag) rows. Prefer the new Models shape; fall back to
    // the legacy ModelIds list by attaching each model's ActiveSignalTag.
    const rowInputs = (s.Models && s.Models.length > 0)
      ? s.Models.map(m => ({ modelId: m.ModelId, tag: m.Tag }))
      : (s.ModelIds || []).map(id => ({ modelId: id, tag: null }));

    if (rowInputs.length > 0 && allModels.length > 0) {
      const rows = rowInputs
        .map(input => {
          const model = allModels.find(m => m.value === input.modelId);
          if (!model) return null;
          // If the saved tag isn't on the model anymore (e.g. tags changed
          // since the settings were saved), drop it so the user has to pick
          // a fresh one rather than running with a stale tag.
          const validTag = input.tag && model.signalTags.includes(input.tag)
            ? input.tag
            : null;
          return { model, tag: validTag };
        })
        .filter(Boolean);
      setSelectedModelRows(rows);
    }
    if (s.InitialAmount != null) setInitialAmount(s.InitialAmount);
    if (s.Stocks?.length) {
      setStocks(s.Stocks.map(st => ({
        tickerId: st.TickerId ? { value: st.TickerId, label: `#${st.TickerId}` } : null,
        percentage: st.Percentage ?? 0,
      })));
      setEqualShare(false);
    }
    if (s.MoneyMarketTickerId) {
      setMoneyMarketTicker({ value: s.MoneyMarketTickerId, label: `#${s.MoneyMarketTickerId}` });
    } else {
      setMoneyMarketTicker(null);
    }
    setStartDate(s.StartDate ? s.StartDate.substring(0, 10) : '');
    setEndDate(s.EndDate ? s.EndDate.substring(0, 10) : '');
    setUseFastTrack(!!s.UseFastTrack);
    setUseDividendAdjusted(!!(s.UseFastTrack && s.UseDividendAdjustedPrices));
    setCommissionPercent(s.CommissionPercent ?? 0.2);
    const cm = COMBINE_MODE_OPTIONS.find(o => o.value === s.CombineMode) || COMBINE_MODE_OPTIONS[0];
    setCombineMode(cm);
    setSaveSettingsId(ls.Id ?? null);
    setSaveSettingsName(ls.Name ?? '');
    setLoadedSettingsName(ls.Name ?? null);
    // clear the nav state so reloads don't re-apply it
    window.history.replaceState({}, '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, allModels]);

      // ── Chart rendering ────────────────────────────────────────────────────────
    useEffect(() => {
      if (portfolioView !== 'chart' || (!result && !buyAndHoldResult && !buyAndHoldIndexResult) || !chartContainerRef.current) return;

      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }

      const chart = createChart(chartContainerRef.current, {
        width: chartContainerRef.current.clientWidth,
        height: 350,
        layout: { background: { color: '#ffffff' }, textColor: '#333' },
        grid: { vertLines: { color: '#eee' }, horzLines: { color: '#eee' } },
        timeScale: { borderColor: '#ccc' },
        rightPriceScale: { mode: chartScale === 'log' ? 1 : 0 },
        crosshair: { mode: CrosshairMode.Normal },
      });
      chartRef.current = chart;

      // Add series for regular calculation (if available)
      let lineSeries = null;
      if (result) {
        lineSeries = chart.addLineSeries({ color: '#0d6efd', lineWidth: 2, title: 'Portfolio Value' });
      }

      // Add series for BuyAndHold (if available)
      let buyAndHoldLineSeries = null;
      if (buyAndHoldResult) {
        buyAndHoldLineSeries = chart.addLineSeries({ color: '#198754', lineWidth: 2, title: 'Buy & Hold Portfolio' });
      }

      // Add series for BuyAndHold S&P (if available)
      let buyAndHoldIndexLineSeries = null;
      if (buyAndHoldIndexResult) {
        buyAndHoldIndexLineSeries = chart.addLineSeries({ color: '#dc3545', lineWidth: 2, title: 'Buy & Hold Index Portfolio' });
      }

     const addDataToSeries = (series, portfolioHistory) => {
       if (!series) return;
       const data = (portfolioHistory || [])
         .map(p => ({
           time: p.Date.substring(0, 10),
           value: p.TotalValue,
         }))
         .filter((p, i, arr) => i === 0 || p.time !== arr[i - 1].time); // deduplicate

       series.setData(data);
     };

      addDataToSeries(lineSeries, result?.PortfolioHistory);
      addDataToSeries(buyAndHoldLineSeries, buyAndHoldResult?.PortfolioHistory);
      addDataToSeries(buyAndHoldIndexLineSeries, buyAndHoldIndexResult?.PortfolioHistory);

      chart.timeScale().fitContent();

     const handleResize = () => {
       if (chartContainerRef.current && chartRef.current) {
         chartRef.current.applyOptions({ width: chartContainerRef.current.clientWidth });
       }
     };
     window.addEventListener('resize', handleResize);
     return () => {
       window.removeEventListener('resize', handleResize);
       if (chartRef.current) {
         chartRef.current.remove();
         chartRef.current = null;
       }
     };
    }, [portfolioView, result, buyAndHoldResult, buyAndHoldIndexResult, chartScale, chartType]);

  // ── Annual return histogram chart ──────────────────────────────────────────
  const computeAnnualReturns = (portfolioHistory, timeOffset = 0) => {
    if (!portfolioHistory || portfolioHistory.length < 2) return [];
    const yearGroups = {};
    portfolioHistory.forEach(p => {
      const year = p.Date.substring(0, 4);
      if (!yearGroups[year]) yearGroups[year] = [];
      yearGroups[year].push(p);
    });
    const result = [];
    Object.keys(yearGroups).sort().forEach(year => {
      const entries = yearGroups[year];
      if (entries.length < 2) return;
      const firstValue = entries[0].TotalValue;
      const lastValue = entries[entries.length - 1].TotalValue;
      const returnPct = ((lastValue - firstValue) / firstValue) * 100;
      const ts = Math.floor(new Date(entries[entries.length - 1].Date).getTime() / 1000) + timeOffset;
      result.push({
        time: ts,
        value: Math.round(returnPct * 100) / 100,
      });
    });
    return result;
  };

  useEffect(() => {
    if (!result || !annualReturnChartContainerRef.current) return;
    if (annualReturnChartRef.current) {
      annualReturnChartRef.current.remove();
      annualReturnChartRef.current = null;
    }
    const chart = createChart(annualReturnChartContainerRef.current, {
      width: annualReturnChartContainerRef.current.clientWidth,
      height: 250,
      layout: { background: { color: '#ffffff' }, textColor: '#333' },
      grid: { vertLines: { color: '#eee' }, horzLines: { color: '#eee' } },
      timeScale: { borderColor: '#ccc' },
      rightPriceScale: { mode: 0 },
      crosshair: { mode: CrosshairMode.Normal },
    });
    annualReturnChartRef.current = chart;

    const strategyReturns = computeAnnualReturns(result?.PortfolioHistory);
    if (strategyReturns.length > 0) {
      const s = chart.addHistogramSeries({
        color: '#0d6efd',
        priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
        title: 'Strategy',
      });
      s.setData(strategyReturns);
    }

    if (buyAndHoldResult) {
      const bhReturns = computeAnnualReturns(buyAndHoldResult?.PortfolioHistory, 15 * 86400);
      if (bhReturns.length > 0) {
        const s = chart.addHistogramSeries({
          color: '#198754',
          priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
          title: 'Buy & Hold',
        });
        s.setData(bhReturns);
      }
    }

    if (buyAndHoldIndexResult) {
      const bhiReturns = computeAnnualReturns(buyAndHoldIndexResult?.PortfolioHistory, 30 * 86400);
      if (bhiReturns.length > 0) {
        const s = chart.addHistogramSeries({
          color: '#dc3545',
          priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
          title: 'Buy & Hold Index',
        });
        s.setData(bhiReturns);
      }
    }

    chart.timeScale().fitContent();

    const handleResize = () => {
      if (annualReturnChartContainerRef.current && annualReturnChartRef.current) {
        annualReturnChartRef.current.applyOptions({ width: annualReturnChartContainerRef.current.clientWidth });
      }
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (annualReturnChartRef.current) {
        annualReturnChartRef.current.remove();
        annualReturnChartRef.current = null;
      }
    };
  }, [result, buyAndHoldResult, buyAndHoldIndexResult]);

  // ── Ticker async search ───────────────────────────────────────────────────
  async function loadTickerOptions(inputValue, callback) {
    if (!inputValue || inputValue.length < 2) {
      callback([]);
      return;
    }
    try {
      const data = await ApiClient.searchTickerItems({ SearchTerm: inputValue, Limit: 20, Offset: 0 });
      const options = (data || []).map(t => ({
        value: t.Id,
        label: `${t.Code || ''}${t.Name ? ' ' + t.Name : ''} [${t.Source || ''}]`,
      }));
      callback(options);
    } catch (err) {
      console.error('Ticker search failed', err);
      callback([]);
    }
  }

  // ── Model async search ────────────────────────────────────────────────────
  // AsyncSelect is used here instead of the static `allModels` list so the
  // user can search by name; we still want SignalTags / ActiveSignalTag
  // carried into the row so the tag picker can populate itself from the
  // model's tags without an extra round-trip.
  async function loadModelOptions(inputValue, callback) {
    if (!inputValue || inputValue.length < 1) {
      callback([]);
      return;
    }
    try {
      const data = await ApiClient.searchModels({ NamePartial: inputValue, Offset: 0, Limit: 30 });
      const options = (data.Items || []).map(m => ({
        value: m.Id,
        label: `${m.Name || m.FileName} [#${m.Id}]`,
        signalTags: m.SignalTags || [],
        activeSignalTag: m.ActiveSignalTag || null,
      }));
      callback(options);
    } catch (err) {
      console.error('Model search failed', err);
      callback([]);
    }
  }

  // ── Stocks helpers ─────────────────────────────────────────────────────────
  const distributeEqually = (stocksList) => {
    const count = stocksList.length;
    if (count === 0) return stocksList;
    const base = Math.floor(100 / count);
    const remainder = 100 - (base * count);
    return stocksList.map((stock, idx) => ({
      ...stock,
      percentage: idx === count - 1 ? base + remainder : base
    }));
  };

  const addStock = () => {
    const newStocks = [...stocks, { tickerId: null, percentage: 0 }];
    setStocks(equalShare ? distributeEqually(newStocks) : newStocks);
  };

  const removeStock = idx => {
    const newStocks = stocks.filter((_, i) => i !== idx);
    setStocks(equalShare ? distributeEqually(newStocks) : newStocks);
  };

  const updateStock = (idx, field, value) => {
    if (field === 'percentage') {
      setEqualShare(false);
    }
    setStocks(s => s.map((row, i) => i === idx ? { ...row, [field]: value } : row));
  };

  const handleEqualShareChange = (checked) => {
    setEqualShare(checked);
    if (checked) {
      setStocks(distributeEqually(stocks));
    }
  };

  const percentSum = stocks.reduce((acc, s) => acc + (parseFloat(s.percentage) || 0), 0);

  // ── Checkbox click handlers for request flags ─────────────────────────────
  const handleBuyAndHoldCheckboxChange = (checked) => {
    setRequestBuyAndHold(checked);
  };

  const handleBuyAndHoldIndexCheckboxChange = (checked) => {
    setRequestBuyAndHoldIndex(checked);
    if (!checked) setBuyAndHoldIndexTicker(null);
  };

  // ── Save settings ─────────────────────────────────────────────────────────
  const buildSettingsBody = () => ({
    Models: selectedModelRows
      .filter(r => r.model && r.tag)
      .map(r => ({ ModelId: r.model.value, Tag: r.tag.value })),
    InitialAmount: parseFloat(initialAmount) || 10000,
    Stocks: stocks.map(s => ({ TickerId: s.tickerId?.value ?? null, Percentage: parseFloat(s.percentage) || 0 })),
    MoneyMarketTickerId: moneyMarketTicker ? moneyMarketTicker.value : null,
    UseFastTrack: useFastTrack,
    UseDividendAdjustedPrices: useFastTrack && useDividendAdjusted,
    StartDate: startDate || null,
    EndDate: endDate || null,
    CombineMode: combineMode.value,
    CommissionPercent: parseFloat(commissionPercent) || 0,
  });

  const handleSaveSettings = async () => {
    if (!saveSettingsName.trim()) { setSaveSettingsError('Name is required'); return; }
    setSavingSettings(true);
    setSaveSettingsError('');
    try {
      const res = await ApiClient.savePerformanceCalculationSettings({
        Id: saveSettingsId || null,
        Name: saveSettingsName.trim(),
        Settings: buildSettingsBody(),
      });
      setSaveSettingsId(res.id);
      setLoadedSettingsName(saveSettingsName.trim());
      setShowSaveModal(false);
    } catch (exc) {
      setSaveSettingsError(exc.message || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  // ── Calculate ─────────────────────────────────────────────────────────────
  const handleCalculate = async () => {
    setError(null);
    setResult(null);
    setBuyAndHoldResult(null);
    setBuyAndHoldIndexResult(null);

    if (selectedModelRows.length === 0) { setError('Add at least one model'); return; }
    if (selectedModelRows.some(r => !r.model)) { setError('Every model row must have a model selected'); return; }
    if (selectedModelRows.some(r => !r.tag)) { setError('Every model row must have a tag selected'); return; }
    if (stocks.some(s => !s.tickerId)) { setError('All stock rows must have a ticker selected'); return; }
    if (Math.abs(percentSum - 100) > 0.01) { setError(`Stock percentages must sum to 100% (currently ${percentSum.toFixed(2)}%)`); return; }

      const baseBody = {
        Models: selectedModelRows.map(r => ({ ModelId: r.model.value, Tag: r.tag.value })),
        InitialAmount: parseFloat(initialAmount) || 10000,
        Stocks: stocks.map(s => ({ TickerId: s.tickerId.value, Percentage: parseFloat(s.percentage) })),
        MoneyMarketTickerId: moneyMarketTicker ? moneyMarketTicker.value : null,
        UseFastTrack: useFastTrack,
        UseDividendAdjustedPrices: useFastTrack && useDividendAdjusted,
        StartDate: startDate || null,
        EndDate: endDate || null,
        CombineMode: combineMode.value,
        CommissionPercent: parseFloat(commissionPercent) || 0,
      };

      setLoading(true);
      try {
        const data = await ApiClient.calculatePerformance(baseBody);
        setResult(data);
      } catch (exc) {
        setError(exc.message || 'Calculation failed');
      } finally {
        setLoading(false);
      };

      // ── Handle BuyAndHold request ───────────────────────────────────────
      if (requestBuyAndHold) {
        try {
          const data = await ApiClient.calculateBuyAndHold(baseBody);
          setBuyAndHoldResult(data);
        } catch (exc) {
          console.error('BuyAndHold calculation failed:', exc);
        }
      }

      // ── Handle BuyAndHoldIndex request ─────────────────────────────────────
      if (requestBuyAndHoldIndex && buyAndHoldIndexTicker) {
        try {
          const indexBody = { ...baseBody, IndexTickerId: buyAndHoldIndexTicker.value };
          const data = await ApiClient.calculateBuyAndHoldIndex(indexBody);
          setBuyAndHoldIndexResult(data);
        } catch (exc) {
          console.error('BuyAndHold Index calculation failed:', exc);
        }
      }
    };

  // ── Portfolio summary ──────────────────────────────────────────────────────
  const finalValue = result?.PortfolioHistory?.length
    ? result.PortfolioHistory[result.PortfolioHistory.length - 1].TotalValue
    : null;
  const gain = finalValue != null ? finalValue - initialAmount : null;
  const gainPct = gain != null && initialAmount > 0 ? (gain / initialAmount * 100) : null;

  const buyAndHoldFinalValue = buyAndHoldResult?.PortfolioHistory?.length
    ? buyAndHoldResult.PortfolioHistory[buyAndHoldResult.PortfolioHistory.length - 1].TotalValue
    : null;
  const buyAndHoldGain = buyAndHoldFinalValue != null ? buyAndHoldFinalValue - initialAmount : null;
  const buyAndHoldGainPct = buyAndHoldGain != null && initialAmount > 0 ? (buyAndHoldGain / initialAmount * 100) : null;

  const buyAndHoldIndexFinalValue = buyAndHoldIndexResult?.PortfolioHistory?.length
    ? buyAndHoldIndexResult.PortfolioHistory[buyAndHoldIndexResult.PortfolioHistory.length - 1].TotalValue
    : null;
  const buyAndHoldIndexGain = buyAndHoldIndexFinalValue != null ? buyAndHoldIndexFinalValue - initialAmount : null;
  const buyAndHoldIndexGainPct = buyAndHoldIndexGain != null && initialAmount > 0 ? (buyAndHoldIndexGain / initialAmount * 100) : null;

  return (
    <div>
      <div className="d-flex align-items-center gap-3 mb-3">
        <h2 className="mb-0">Performance Calculation</h2>
        <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/performance-calculation/settings')}>
          Saved Settings
        </button>
        <button
          className="btn btn-outline-primary btn-sm"
          onClick={() => { setSaveSettingsError(''); setShowSaveModal(true); }}
        >
          {saveSettingsId ? 'Update Settings' : 'Save Settings'}
        </button>
        {loadedSettingsName && (
          <span className="badge bg-info text-dark">Loaded: {loadedSettingsName}</span>
        )}
      </div>

      {/* ── Save settings modal ────────────────────────────────────────────── */}
      {showSaveModal && (
        <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{saveSettingsId ? 'Update Settings' : 'Save Settings'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowSaveModal(false)} />
              </div>
              <div className="modal-body">
                {saveSettingsError && <div className="alert alert-danger py-2">{saveSettingsError}</div>}
                <label className="form-label fw-semibold">Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={saveSettingsName}
                  onChange={e => setSaveSettingsName(e.target.value)}
                  autoFocus
                  onKeyDown={e => e.key === 'Enter' && handleSaveSettings()}
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowSaveModal(false)}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={handleSaveSettings} disabled={savingSettings}>
                  {savingSettings ? <><span className="spinner-border spinner-border-sm me-2" />Saving…</> : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Setup form ────────────────────────────────────────────────────── */}
      <div className="card mb-3">
        <div className="card-header bg-light">
          <h5 className="mb-0">Performance Calculation - Buy & Hold Strategies</h5>
          <small className="text-muted">Select at least one model and choose a Buy & Hold strategy to compare portfolio performance against the calculated result.</small>
        </div>
        <div className="card-body">
          <div className="row g-3">
            {/* S&P BuyAndHold indicator */}
            {/* Models — one row per model, each row picks the model AND its exact signal tag */}
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="form-label fw-semibold mb-0">Models &amp; Tags</label>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => setSelectedModelRows(rows => [...rows, { model: null, tag: null }])}
                >
                  + Add Model
                </button>
              </div>
              {selectedModelRows.length === 0 && (
                <p className="text-muted small mb-0">
                  No models yet — click <strong>+ Add Model</strong> to add one. Each row picks a model and the exact signal tag whose signals should be used in the calculation.
                </p>
              )}
              {selectedModelRows.map((row, idx) => {
                const tagOptions = (row.model?.signalTags || []).map(t => ({ value: t, label: t }));
                const activeTag = row.model?.activeSignalTag || null;
                return (
                  <div key={idx} className="d-flex gap-2 align-items-center mb-2">
                    <div style={{ flex: 3 }}>
                      <AsyncSelect
                        loadOptions={loadModelOptions}
                        value={row.model ? { value: row.model.value, label: row.model.label } : null}
                        onChange={opt => {
                          setSelectedModelRows(rows => rows.map((r, i) => {
                            if (i !== idx) return r;
                            // Reset the tag whenever the model changes — the
                            // previous tag may not be valid on the new model.
                            return { model: opt, tag: null };
                          }));
                        }}
                        placeholder="Type to search model..."
                        isClearable
                        defaultOptions={false}
                      />
                    </div>
                    <div style={{ flex: 2 }}>
                      <Select
                        options={tagOptions}
                        value={row.tag}
                        onChange={opt => setSelectedModelRows(rows => rows.map((r, i) => i === idx ? { ...r, tag: opt } : r))}
                        placeholder={row.model ? 'Pick a tag' : 'Pick a model first'}
                        isDisabled={!row.model || tagOptions.length === 0}
                        isClearable
                      />
                    </div>
                    {row.model && activeTag && (
                      <small className="text-muted" style={{ whiteSpace: 'nowrap' }} title={`Model's ActiveSignalTag — used for trading strategies and other features`}>
                        active: {activeTag}
                      </small>
                    )}
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => setSelectedModelRows(rows => rows.filter((_, i) => i !== idx))}
                      title="Remove"
                    >✕</button>
                  </div>
                );
              })}
            </div>

            {/* Initial amount */}
            <div className="col-md-4">
              <label className="form-label fw-semibold">Initial Amount (USD)</label>
              <input
                type="number"
                className="form-control"
                value={initialAmount}
                min={1}
                onChange={e => setInitialAmount(e.target.value)}
              />
            </div>

            {/* Money market ticker */}
            <div className="col-md-8">
              <label className="form-label fw-semibold">
                Money Market Ticker <span className="text-muted fw-normal">(optional)</span>
              </label>
              <AsyncSelect
                loadOptions={loadTickerOptions}
                value={moneyMarketTicker}
                onChange={setMoneyMarketTicker}
                placeholder="Type to search money market ticker (optional)..."
                isClearable
                defaultOptions={false}
              />
              <div className="form-text">
                When omitted, Money signals liquidate stock positions to cash and hold cash.
              </div>
            </div>

            {/* Date range */}
            <div className="col-md-4">
              <label className="form-label fw-semibold">
                Start Date <span className="text-muted fw-normal">(optional)</span>
              </label>
              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
              />
              <div className="form-text">
                If blank, auto-detected from first date all tickers have price data.
              </div>
            </div>
            <div className="col-md-4">
              <label className="form-label fw-semibold">
                End Date <span className="text-muted fw-normal">(optional)</span>
              </label>
              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
              />
              <div className="form-text">If blank, defaults to today.</div>
            </div>

            {/* Stocks */}
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="form-label fw-semibold mb-0">
                  Stocks &nbsp;
                  <span className={`badge bg-${Math.abs(percentSum - 100) < 0.01 ? 'success' : 'danger'}`}>
                    {percentSum.toFixed(1)}%
                  </span>
                </label>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="equalShareCheck"
                    checked={equalShare}
                    onChange={e => handleEqualShareChange(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="equalShareCheck">
                    Equal share
                  </label>
                </div>
              </div>
              {stocks.map((row, idx) => (
                <div key={idx} className="d-flex gap-2 align-items-center mb-2">
                  <div style={{ flex: 3 }}>
                    <AsyncSelect
                      loadOptions={loadTickerOptions}
                      value={row.tickerId}
                      onChange={opt => updateStock(idx, 'tickerId', opt)}
                      placeholder="Type to search ticker..."
                      isClearable
                      defaultOptions={false}
                    />
                  </div>
                  <div style={{ width: 120 }}>
                    <div className="input-group">
                      <input
                        type="number"
                        className="form-control"
                        value={row.percentage}
                        min={0}
                        max={100}
                        step={0.1}
                        onChange={e => updateStock(idx, 'percentage', e.target.value)}
                      />
                      <span className="input-group-text">%</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => removeStock(idx)}
                    disabled={stocks.length === 1}
                    title="Remove"
                  >✕</button>
                </div>
              ))}
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={addStock}>
                + Add Stock
              </button>
            </div>

          </div>

          {/* Signal combination mode (only meaningful with ≥ 2 models) */}
          <div className="col-12 mt-2">
            <label className="form-label fw-semibold">Signal Combination Mode</label>
            <Select
              options={COMBINE_MODE_OPTIONS}
              value={combineMode}
              onChange={setCombineMode}
              isDisabled={selectedModelRows.length < 2}
              placeholder="Select combination mode..."
            />
            <div className="form-text">
              How to combine signals from multiple models. Only applies when 2+ models are selected.
            </div>
          </div>

          {/* FastTrack options */}
          <div className="col-12 mt-2">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="useFastTrack"
                checked={useFastTrack}
                onChange={e => {
                  setUseFastTrack(e.target.checked);
                  if (!e.target.checked) setUseDividendAdjusted(false);
                }}
              />
              <label className="form-check-label" htmlFor="useFastTrack">
                Use FastTrack for performance calculation
              </label>
            </div>
            {useFastTrack && (
              <div className="form-check ms-4 mt-1">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="useDividendAdjusted"
                  checked={useDividendAdjusted}
                  onChange={e => setUseDividendAdjusted(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="useDividendAdjusted">
                  Use dividend-adjusted prices
                </label>
                <div className="form-text ms-4 mt-1">
                  When enabled, any dividend cash the position is entitled to on
                  ex-dividend dates is credited to cash (and shown as a <code>Dividend</code> trade).
                </div>
              </div>
            )}
          </div>

          {/* Commission */}
          <div className="col-12 mt-2">
            <label className="form-label fw-semibold" htmlFor="commissionPercent">
              Commission (%)
            </label>
            <div className="input-group" style={{ maxWidth: 200 }}>
              <input
                id="commissionPercent"
                type="number"
                className="form-control"
                value={commissionPercent}
                min={0}
                max={100}
                step={0.01}
                onChange={e => setCommissionPercent(e.target.value)}
              />
              <span className="input-group-text">%</span>
            </div>
            <div className="form-text">
              Applied to every executed trade (buy or sell, including money-market trades). Default 0.2%.
              Set to 0 to disable commissions.
            </div>
          </div>

          {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}

            <div className="mt-3 d-flex flex-column gap-2">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="buyAndHoldCheck"
                  checked={requestBuyAndHold}
                  onChange={e => handleBuyAndHoldCheckboxChange(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="buyAndHoldCheck">
                  <span className="text-success">✓</span> Buy & Hold
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="buyAndHoldIndexCheck"
                  checked={requestBuyAndHoldIndex}
                  onChange={e => handleBuyAndHoldIndexCheckboxChange(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="buyAndHoldIndexCheck">
                  <span className="text-danger">✓</span> Buy & Hold Index
                </label>
              </div>
              {requestBuyAndHoldIndex && (
                <div>
                  <label className="form-label fw-semibold">Index Ticker</label>
                  <AsyncSelect
                    loadOptions={loadTickerOptions}
                    value={buyAndHoldIndexTicker}
                    onChange={setBuyAndHoldIndexTicker}
                    placeholder="Type to search index ticker..."
                    isClearable
                    defaultOptions={false}
                  />
                  <div className="form-text">
                    Select the index ticker to invest in (e.g., SPY for S&P 500, VTI for Total Market, QQQ for NASDAQ).
                  </div>
                </div>
              )}
            <button
              type="button"
              className="btn btn-primary btn-sm align-self-start"
              onClick={handleCalculate}
              disabled={loading}
            >
              {loading ? <><span className="spinner-border spinner-border-sm me-2" />Calculating...</> : 'Calculate'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Results ───────────────────────────────────────────────────────── */}
      {result && (
        <>
          {/* Summary */}
          {(finalValue != null || buyAndHoldFinalValue != null || buyAndHoldIndexFinalValue != null) && (
            <div className="row g-3 mb-3">
              <div className="col-md-4">
                <div className="card text-center">
                  <div className="card-body">
                    <div className="text-muted small">Initial</div>
                    <div className="fs-5 fw-bold">${parseFloat(initialAmount).toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                  </div>
                </div>
              </div>
              {finalValue != null && (
                <div className="col-md-8">
                  <div className="row g-3">
                    <div className="col-6 d-flex flex-column gap-3">
                      <div className="card text-center">
                        <div className="card-body">
                          <div className="text-muted small">Final Value</div>
                          <div className="fs-5 fw-bold">${finalValue.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                        </div>
                      </div>
                      {buyAndHoldFinalValue != null && (
                        <div className="card text-center border-success">
                          <div className="card-body">
                            <div className="text-muted small">Buy & Hold Final Value</div>
                            <div className="fs-5 fw-bold text-success">${buyAndHoldFinalValue.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                          </div>
                        </div>
                      )}
                      {buyAndHoldIndexFinalValue != null && (
                        <div className="card text-center border-danger">
                          <div className="card-body">
                            <div className="text-muted small">Buy & Hold S&P Final Value</div>
                            <div className="fs-5 fw-bold text-danger">${buyAndHoldIndexFinalValue.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="col-6 d-flex flex-column gap-3">
                      <div className={`card text-center border-${gain >= 0 ? 'success' : 'danger'}`}>
                        <div className="card-body">
                          <div className="text-muted small">Gain / Loss</div>
                          <div className={`fs-5 fw-bold text-${gain >= 0 ? 'success' : 'danger'}`}>
                            {gain >= 0 ? '+' : ''}{gain.toLocaleString('en-US', { minimumFractionDigits: 2 })} ({gainPct >= 0 ? '+' : ''}{gainPct.toFixed(2)}%)
                          </div>
                        </div>
                      </div>
                      {buyAndHoldGain != null && (
                        <div className={`card text-center border-${buyAndHoldGain >= 0 ? 'success' : 'danger'}`}>
                          <div className="card-body">
                            <div className="text-muted small">Buy & Hold Gain / Loss</div>
                            <div className={`fs-5 fw-bold text-${buyAndHoldGain >= 0 ? 'success' : 'danger'}`}>
                              {buyAndHoldGain >= 0 ? '+' : ''}{buyAndHoldGain.toLocaleString('en-US', { minimumFractionDigits: 2 })} ({buyAndHoldGainPct >= 0 ? '+' : ''}{buyAndHoldGainPct.toFixed(2)}%)
                            </div>
                          </div>
                        </div>
                      )}
                      {buyAndHoldIndexGain != null && (
                        <div className={`card text-center border-${buyAndHoldIndexGain >= 0 ? 'success' : 'danger'}`}>
                          <div className="card-body">
                            <div className="text-muted small">Buy & Hold S&P Gain / Loss</div>
                            <div className={`fs-5 fw-bold text-${buyAndHoldIndexGain >= 0 ? 'success' : 'danger'}`}>
                              {buyAndHoldIndexGain >= 0 ? '+' : ''}{buyAndHoldIndexGain.toLocaleString('en-US', { minimumFractionDigits: 2 })} ({buyAndHoldIndexGainPct >= 0 ? '+' : ''}{buyAndHoldIndexGainPct.toFixed(2)}%)
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Portfolio chart / table */}
          <div className="card mb-3">
            <div
              className="card-header d-flex justify-content-between align-items-center"
              style={{ cursor: 'pointer' }}
              onClick={() => setShowPortfolio(v => !v)}
            >
              <span className="fw-semibold">Portfolio Value Over Time</span>
              <div className="d-flex gap-2" onClick={e => e.stopPropagation()}>
                <div className="btn-group btn-group-sm">
                  <button className={`btn btn-outline-secondary ${portfolioView === 'chart' ? 'active' : ''}`} onClick={() => setPortfolioView('chart')}>Chart</button>
                  <button className={`btn btn-outline-secondary ${portfolioView === 'table' ? 'active' : ''}`} onClick={() => setPortfolioView('table')}>Table</button>
                </div>
                <div className="btn-group btn-group-sm">
                  <button
                    className={`btn btn-outline-secondary ${chartScale === 'lin' ? 'active' : ''}`}
                    onClick={() => setChartScale('lin')}
                    title="Linear scale"
                  >
                    Linear
                  </button>
                  <button
                    className={`btn btn-outline-secondary ${chartScale === 'log' ? 'active' : ''}`}
                    onClick={() => setChartScale('log')}
                    title="Log scale"
                  >
                    Logarithmic
                  </button>
                </div>
                <span>{showPortfolio ? '▲' : '▼'}</span>
              </div>
            </div>
            {showPortfolio && (
              <div className="card-body">
                {portfolioView === 'chart' ? (
                  <div ref={chartContainerRef} style={{ width: '100%', height: 350 }} />
                ) : (
                  <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                    <table className="table table-sm table-striped table-hover">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th>Date</th>
                          <th className="text-end">Total Value</th>
                          <th className="text-end">Cash</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.PortfolioHistory.map((p, i) => (
                          <tr key={i}>
                            <td>{p.Date.substring(0, 10)}</td>
                            <td className="text-end">${p.TotalValue.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                            <td className="text-end">${p.Cash.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Annual Return histogram */}
          <div className="card mb-3">
            <div className="card-header fw-semibold">Annual Return</div>
            <div className="card-body">
              <div ref={annualReturnChartContainerRef} style={{ width: '100%', height: 250 }} />
            </div>
          </div>

          {/* Trades */}
          <div className="card mb-3">
            <div
              className="card-header d-flex justify-content-between align-items-center"
              style={{ cursor: 'pointer' }}
              onClick={() => setShowTrades(v => !v)}
            >
              <span className="fw-semibold">Trades ({result.Trades?.length ?? 0})</span>
              <span>{showTrades ? '▲' : '▼'}</span>
            </div>
            {showTrades && (
              <div className="card-body p-0">
                <div style={{ maxHeight: 350, overflowY: 'auto' }}>
                  <table className="table table-sm table-hover mb-0">
                    <thead className="table-light sticky-top">
                      <tr>
                        <th>Date</th>
                        <th>Action</th>
                        <th>Ticker</th>
                        <th className="text-end">Price</th>
                        <th className="text-end">Shares</th>
                        <th className="text-end">Amount</th>
                        <th className="text-end">Commission</th>
                        <th className="text-end">Cash After</th>
                        <th>Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(result.Trades || []).map((t, i) => (
                        <tr key={i}>
                          <td>{t.Date.substring(0, 10)}</td>
                          <td>
                            <span className={`badge bg-${ACTION_BADGE[t.Action] || 'secondary'}`}>
                              {ACTION_LABELS[t.Action] || t.Action}
                            </span>
                          </td>
                          <td>{t.TickerCode}</td>
                          <td>{t.Price > 0 ? `$${t.Price.toFixed(2)}` : '–'}</td>
                          <td className="text-end">{t.Shares > 0 ? Math.round(t.Shares) : '–'}</td>
                          <td className={`text-end ${t.Amount >= 0 ? 'text-success' : 'text-danger'}`}>
                            {t.Amount !== 0 ? `${t.Amount >= 0 ? '+' : ''}$${t.Amount.toFixed(2)}` : '–'}
                          </td>
                          <td className="text-end text-muted">
                            {t.Commission > 0 ? `$${t.Commission.toFixed(2)}` : '–'}
                          </td>
                          <td className="text-end text-muted">${t.RemainingCash != null ? t.RemainingCash.toFixed(2) : '–'}</td>
                          <td className="text-muted small">{t.Note || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Combined signals */}
          <div className="card mb-3">
            <div
              className="card-header d-flex justify-content-between align-items-center"
              style={{ cursor: 'pointer' }}
              onClick={() => setShowCombinedSignals(v => !v)}
            >
              <span className="fw-semibold">Combined Signals ({result.CombinedSignals?.length ?? 0})</span>
              <span>{showCombinedSignals ? '▲' : '▼'}</span>
            </div>
            {showCombinedSignals && (
              <div className="card-body p-0">
                <SignalTable signals={result.CombinedSignals} />
              </div>
            )}
          </div>

          {/* Per-model signals */}
          <div className="card mb-3">
            <div
              className="card-header d-flex justify-content-between align-items-center"
              style={{ cursor: 'pointer' }}
              onClick={() => setShowModelSignals(v => !v)}
            >
              <span className="fw-semibold">Signals per Model</span>
              <span>{showModelSignals ? '▲' : '▼'}</span>
            </div>
            {showModelSignals && (
              <div className="card-body">
                {(result.ModelSignals || []).map((ms, i) => (
                  <div key={i} className="mb-3">
                    <h6 className="fw-semibold">
                      {ms.ModelName} <span className="text-muted fw-normal">(ID {ms.ModelId})</span>
                      {ms.Tag && <span className="badge bg-info text-dark ms-2">tag: {ms.Tag}</span>}
                    </h6>
                    <SignalTable signals={ms.Signals} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

function SignalTable({ signals }) {
  if (!signals || signals.length === 0)
    return <p className="text-muted px-3 py-2 mb-0">No signals</p>;
  return (
    <div style={{ maxHeight: 250, overflowY: 'auto' }}>
      <table className="table table-sm table-hover mb-0">
        <thead className="table-light sticky-top">
          <tr>
            <th>Date</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {signals.map((s, i) => (
            <tr key={i}>
              <td>{(s.Time || s.time || '').substring(0, 10)}</td>
              <td>
                <span className={`badge bg-${SIGNAL_BADGE[s.Action || s.action] || 'secondary'}`}>
                  {s.Action || s.action}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
